import { create } from "zustand";

import type { StoryboardErrorType } from "@/components/Canvas/ErrorOverlay/ErrorOverlay";

export interface DevErrorJson {
  message: string;
  filename: string;
  stack: string;
  count: number;

  lineno?: number;
  colno?: number;
  storyboards?: string[];
  suggestion?: string;
  errorType?: StoryboardErrorType;
}

export enum ServerCommandType {
  RUN_CONTAINER = "RUN_CONTAINER",
  START_SERVER = "START_SERVER",
}

export interface RunningServerCommand {
  commandType: ServerCommandType;
  startTime: Date;
  abortController?: AbortController;
}

const MAX_ERRORS = 20;

interface DevServerStore {
  terminalErrors: DevErrorJson[];
  addTerminalErrors: (terminalErrors: DevErrorJson[]) => void;
  clearTerminalErrors: () => void;

  storyboardIdToStatus: {
    [storyboardId: string]: {
      status: "offline" | "frozen" | "live" | "loading";
      last_updated: Date;
    };
  };
  setStoryboardIdToStatus: (
    storyboardId: string,
    newStatus: {
      status: "offline" | "frozen" | "live" | "loading";
      last_updated: Date;
    },
  ) => void;

  // Errors caught by the error boundary or error script or hydration error
  storyboardIdToErrors: {
    [storyboardId: string]: DevErrorJson[];
  };
  addErrorForStoryboard: (storyboardId: string, error: DevErrorJson) => void;
  clearErrorsForStoryboard: (storyboardId: string) => void;

  // Warning from nav tree not loading
  storyboardIdToSlowToLoad: {
    [storyboardId: string]: boolean;
  };
  setSlowToLoadForStoryboard: (
    storyboardId: string,
    slowToLoad: boolean,
  ) => void;

  // Nav tree failed to load
  storyboardIdToManualFailedToLoad: {
    [storyboardId: string]: boolean;
  };
  setManualFailedToLoadForStoryboard: (
    storyboardId: string,
    failedToLoad: boolean,
  ) => void;

  resetTimeoutsTrigger: {
    [storyboardId: string]: number;
  };
  clearAllErrors: () => void;

  // To track if there is any command that is starting the server or not
  runningServerCommands: RunningServerCommand[];
  setRunningServerCommands: (commands: RunningServerCommand[]) => void;
  resetDevServerStore: () => void;
}

export const useDevServerStore = create<DevServerStore>((set, get) => ({
  terminalErrors: [],
  addTerminalErrors: (terminalErrors: DevErrorJson[]) => {
    set((state) => ({
      terminalErrors: [...state.terminalErrors, ...terminalErrors].slice(
        0,
        MAX_ERRORS,
      ),
    }));
  },
  clearTerminalErrors: () => {
    set({ terminalErrors: [] });
  },

  storyboardIdToStatus: {},
  setStoryboardIdToStatus: (
    storyboardId: string,
    newStatus: {
      status: "offline" | "frozen" | "live" | "loading";
      last_updated: Date;
    },
  ) => {
    set((state) => ({
      storyboardIdToStatus: {
        ...state.storyboardIdToStatus,
        [storyboardId]: newStatus,
      },
    }));
  },

  storyboardIdToErrors: {},
  addErrorForStoryboard: (storyboardId: string, error: DevErrorJson) => {
    set((state) => ({
      storyboardIdToErrors: {
        ...state.storyboardIdToErrors,
        [storyboardId]: [
          ...(state.storyboardIdToErrors[storyboardId] || []),
          error,
        ].slice(0, MAX_ERRORS),
      },
    }));
  },
  clearErrorsForStoryboard: (storyboardId: string) => {
    set((state) => {
      const newErrors = { ...state.storyboardIdToErrors };
      delete newErrors[storyboardId];
      return {
        storyboardIdToErrors: newErrors,
        resetTimeoutsTrigger: {
          ...state.resetTimeoutsTrigger,
          [storyboardId]: (state.resetTimeoutsTrigger[storyboardId] || 0) + 1,
        },
        storyboardIdToSlowToLoad: {
          ...state.storyboardIdToSlowToLoad,
          [storyboardId]: false,
        },
        storyboardIdToManualFailedToLoad: {
          ...state.storyboardIdToManualFailedToLoad,
          [storyboardId]: false,
        },
      };
    });
  },

  storyboardIdToSlowToLoad: {},
  setSlowToLoadForStoryboard: (storyboardId: string, slowToLoad: boolean) => {
    set((state) => ({
      storyboardIdToSlowToLoad: {
        ...state.storyboardIdToSlowToLoad,
        [storyboardId]: slowToLoad,
      },
    }));
  },

  storyboardIdToManualFailedToLoad: {},
  setManualFailedToLoadForStoryboard: (
    storyboardId: string,
    failedToLoad: boolean,
  ) => {
    set((state) => ({
      storyboardIdToManualFailedToLoad: {
        ...state.storyboardIdToManualFailedToLoad,
        [storyboardId]: failedToLoad,
      },
    }));
  },
  clearAllManualFailedToLoad: () => {
    set({ storyboardIdToManualFailedToLoad: {} });
  },

  resetTimeoutsTrigger: {},

  clearAllErrors: () => {
    set({
      terminalErrors: [],
      storyboardIdToErrors: {},
      storyboardIdToSlowToLoad: {},
      storyboardIdToManualFailedToLoad: {},
      resetTimeoutsTrigger: {},
    });
  },

  resetDevServerStore: () => {
    set({
      terminalErrors: [],
      storyboardIdToStatus: {},
      storyboardIdToErrors: {},
      runningServerCommands: [],
      storyboardIdToSlowToLoad: {},
      storyboardIdToManualFailedToLoad: {},
    });
  },

  runningServerCommands: [],
  setRunningServerCommands: (commands: RunningServerCommand[]) => {
    set({ runningServerCommands: commands });
  },
}));
